import { Injectable } from '@angular/core';
import {IDefaultListResponse} from '@api/models/default-response/response';
import {Router} from '@angular/router';
import {ScrollPositionRestorationService} from '@utils/services/scroll-position-restauration/scroll-position-restoration.service';

type Response<ItemT> = Promise<IDefaultListResponse<ItemT>>
type PaginatedRequest<ItemT> = (...args) => Response<ItemT>;

@Injectable()
export class PaginationService<ItemT> {

  public pageSize = 8;
  public items: ItemT[] = [];
  public paginatedRequest: PaginatedRequest<ItemT>;
  public requestPrefixArgs: string[] = [];
  public requestSuffixArgs: string[] = [];
  public loading = false;
  public loadingMore = false;
  public moreAvailable = false;
  public alwaysUpdate = false;

  constructor( paginatedRequest: PaginatedRequest<ItemT>,
               requestPrefixArgs?: string[],
               requestSuffixArgs?: string[] ) {
    this.paginatedRequest = paginatedRequest;
    if (requestPrefixArgs) {
      this.requestPrefixArgs = requestPrefixArgs;
    }
    if (requestSuffixArgs) {
      this.requestSuffixArgs = requestSuffixArgs;
    }
  }

  public async init(): Promise<void> {
    // Reset stored items if user navigation was imperative
    if (ScrollPositionRestorationService.isImperative) {
      this.items = [];
    }
    try {
      if (!this.alwaysUpdate && this.items.length > 0) {
        return;
      }
      this.loading = this.items.length === 0;
      let limit = this.pageSize;
      if (this.alwaysUpdate) {
        limit = this.items.length > 0 ? this.items.length : this.pageSize;
      }
      const response = await this.request(0, limit);
      if (response.success) {
        this.items = response.data;
        this.moreAvailable = this.items.length < response.total_count;
      }
      this.loading = false
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  public async loadMore(): Promise<boolean> {
    try {
      if (!this.moreAvailable || this.loading || this.loadingMore) {
        return false;
      }
      this.loadingMore = true;
      const response = await this.request(this.items.length, this.pageSize);
      if (response.success) {
        this.items.push(...response.data);
        this.moreAvailable = this.items.length < response.total_count;
      }
      this.loadingMore = false;
      return true;
    } catch (e) {
      this.loadingMore = false;
      throw e;
    }
  }

  private async request(skip = 0, limit = this.pageSize): Response<ItemT> {
    return this.paginatedRequest(...this.requestPrefixArgs, skip, limit, ...this.requestSuffixArgs);
  }
}
